import React, { useEffect, useState, useCallback } from "react";
import {
  useNavigate,
  useParams
} from "react-router-dom";
import {
  ArrowLeft,
  AlertTriangle
} from "lucide-react";

import {
  getProgressOverview
} from "../../api/syllabus/syllabusApi";
import {
  getProgressOverviewPortal
} from "../../api/syllabus/syllabusPortalApi";
import {
  useToast
} from "../../context/ToastContext";

const SyllabusOverview = ({ isPortal = false }) => {
  const { templateId } = useParams();
  const navigate = useNavigate();

  const [template, setTemplate] = useState(null);
  const [sections, setSections] = useState([]);
  const [loading, setLoading] = useState(true);
  const toast = useToast();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = isPortal
        ? await getProgressOverviewPortal(templateId)
        : await getProgressOverview(templateId);
      const data = res.data || res;
      setTemplate(data.template);
      setSections(data.sections || []);
    } catch (err) {
      console.error("Failed to load progress overview:", err);
      toast.error(err?.response?.data?.message || "Failed to load progress overview");
    } finally {
      setLoading(false);
    }
  }, [isPortal, templateId]);

  useEffect(() => {
    load();
  }, [load]);

  const backPath = isPortal ? "/staff-panel/syllabus" : "/syllabus";

  const goToSectionProgress = (sectionId) => {
    const path = isPortal
      ? `/staff-panel/syllabus/progress/${templateId}?sectionId=${sectionId}`
      : `/syllabus/progress/${templateId}?sectionId=${sectionId}`;
    navigate(path);
  };

  if (loading) {
    return <div className="text-center text-neutral-400 dark:text-neutral-500 py-16">Loading overview…</div>;
  }

  if (!template) {
    return <div className="text-center text-neutral-400 dark:text-neutral-500 py-16">Template not found</div>;
  }

  const subject = template.subjectId || {};
  const classInfo = template.classId || {};

  const overallTotals = sections.reduce(
    (acc, s) => {
      acc.totalTopics += s.summary?.totalTopics || 0;
      acc.covered += s.summary?.covered || 0;
      return acc;
    },
    { totalTopics: 0, covered: 0 }
  );
  const overallPercentage =
    overallTotals.totalTopics > 0
      ? Math.round((overallTotals.covered / overallTotals.totalTopics) * 100)
      : 0;

  return (
    <div className="max-w-6xl mx-auto px-4 py-6">
      <button
        onClick={() => navigate(backPath)}
        className="flex items-center gap-1 text-sm text-neutral-500 dark:text-neutral-400 hover:text-neutral-800 dark:text-neutral-100 mb-4"
      >
        <ArrowLeft size={16} /> Back to Templates
      </button>

      <div className="bg-white dark:bg-neutral-900 rounded-2xl shadow-sm p-6 mb-6">
        <div className="flex items-center gap-2 mb-1 flex-wrap">
          <span
            className="text-xs px-2 py-0.5 rounded-lg text-white"
            style={{ backgroundColor: subject.color || "#8b5cf6" }}
          >
            {subject.name || "Subject"}
          </span>
          <span className="bg-neutral-100 dark:bg-neutral-800 dark:bg-neutral-900 text-neutral-600 dark:text-neutral-300 text-xs px-2 py-0.5 rounded-lg">
            {classInfo.className || "Class"}
          </span>
          <span className="text-xs text-neutral-400 dark:text-neutral-500">{template.academicYear}</span>
        </div>
        <div className="flex justify-between items-end flex-wrap gap-4">
          <h1 className="text-xl font-bold text-neutral-800 dark:text-neutral-100">{template.title}</h1>
          <div className="text-right">
            <p className="text-3xl font-bold text-neutral-800 dark:text-neutral-100">{overallPercentage}%</p>
            <p className="text-xs text-neutral-400 dark:text-neutral-500">Overall across {sections.length} sections</p>
          </div>
        </div>
      </div>

      {sections.length === 0 ? (
        <div className="text-center text-neutral-400 dark:text-neutral-500 py-16 bg-white dark:bg-neutral-900 rounded-2xl shadow-sm">
          No sections found for this class
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {sections.map(({ sectionId, summary }) => (
            <div key={sectionId._id} className="bg-white dark:bg-neutral-900 rounded-2xl shadow p-6">
              <div className="flex justify-between items-center mb-3">
                <h3 className="font-bold">Section {sectionId.sectionName}</h3>
                <span className="text-2xl font-bold text-neutral-700 dark:text-neutral-200">
                  {summary.completionPercentage}%
                </span>
              </div>

              <div className="w-full bg-neutral-100 dark:bg-neutral-800 dark:bg-neutral-900 rounded-full h-2 mb-3">
                <div
                  className="bg-green-500 h-2 rounded-full"
                  style={{ width: `${summary.completionPercentage}%` }}
                />
              </div>

              <div className="grid grid-cols-3 gap-2 text-xs text-center">
                <div className="bg-green-50 p-2 rounded-lg">
                  <p className="font-bold text-green-700">{summary.covered}</p>
                  <p className="text-neutral-500 dark:text-neutral-400">Covered</p>
                </div>
                <div className="bg-yellow-50 p-2 rounded-lg">
                  <p className="font-bold text-yellow-700">{summary.inProgress}</p>
                  <p className="text-neutral-500 dark:text-neutral-400">In Progress</p>
                </div>
                <div className="bg-neutral-50 dark:bg-neutral-900 dark:bg-neutral-950 p-2 rounded-lg">
                  <p className="font-bold text-neutral-600 dark:text-neutral-300">{summary.pending}</p>
                  <p className="text-neutral-500 dark:text-neutral-400">Pending</p>
                </div>
              </div>

              {summary.overdue > 0 && (
                <p className="text-xs text-red-500 mt-2 flex items-center gap-1">
                  <AlertTriangle size={12} /> {summary.overdue} topics overdue
                </p>
              )}

              <button
                onClick={() => goToSectionProgress(sectionId._id)}
                className="w-full mt-4 border py-2 rounded-xl text-sm hover:bg-neutral-50 dark:hover:bg-neutral-800"
              >
                View Details
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default SyllabusOverview;